import { useDeleteOpportunityTask } from '@opportunities/-hooks/useDeleteOpportunityTask'
import { useUpdateOpportunityTask } from '@opportunities/-hooks/useUpdateOpportunityTask'
import { useOpportunityTasks } from '@opportunities/$opportunityId/-hooks/useOpportunityTasks'
import type { OpportunityTask } from 'backend/types'
import { useCallback, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { useTaskStatuses } from '@/shared-hooks/useTaskStatuses'
import { createTaskColumns } from '../components/columns'

interface UseTasksTabOptions {
  opportunityId: number
  editable: boolean
}

export function useTasksTab({ opportunityId, editable }: UseTasksTabOptions) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingTask, setEditingTask] = useState<OpportunityTask | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deletingTask, setDeletingTask] = useState<OpportunityTask | null>(null)
  const [sortKey, setSortKey] = useState<string | null>('title')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')

  const { data, isLoading, error } = useOpportunityTasks(opportunityId)

  const { data: statusesData } = useTaskStatuses()
  const statuses = statusesData?.statuses ?? []

  const { mutate: updateTask } = useUpdateOpportunityTask({
    onSuccess: () => {
      toast.success('ステータスを更新しました')
    },
    onError: (error) => {
      toast.error(`ステータス更新に失敗しました: ${error.message}`)
    },
  })

  const { mutate: deleteTask, isPending: isDeleting } = useDeleteOpportunityTask({
    onSuccess: () => {
      toast.success('タスクを削除しました')
      setDeleteDialogOpen(false)
      setDeletingTask(null)
    },
    onError: (error) => {
      toast.error(`タスク削除に失敗しました: ${error.message}`)
    },
  })

  const handleAddTask = useCallback(() => {
    setEditingTask(null)
    setDialogOpen(true)
  }, [])

  const handleEditTask = useCallback((task: OpportunityTask) => {
    setEditingTask(task)
    setDialogOpen(true)
  }, [])

  const handleDeleteTask = useCallback((task: OpportunityTask) => {
    setDeletingTask(task)
    setDeleteDialogOpen(true)
  }, [])

  const handleConfirmDelete = useCallback(() => {
    if (!deletingTask) return
    deleteTask({ opportunityId, taskId: deletingTask.id })
  }, [deletingTask, deleteTask, opportunityId])

  const handleStatusChange = useCallback(
    (taskId: number, statusId: number) => {
      updateTask({ opportunityId, taskId, data: { opportunityTaskStatusId: statusId } })
    },
    [updateTask, opportunityId],
  )

  const handleSortChange = (key: string | null, direction: 'asc' | 'desc') => {
    setSortKey(key)
    setSortDirection(direction)
  }

  const columns = useMemo(
    () =>
      createTaskColumns({
        statuses,
        editable,
        onEdit: handleEditTask,
        onDelete: handleDeleteTask,
        onStatusChange: handleStatusChange,
      }),
    [statuses, editable, handleEditTask, handleDeleteTask, handleStatusChange],
  )

  const tasks = data?.tasks?.filter((t) => !t.disabled_at) ?? []

  return {
    tasks,
    isLoading,
    error,
    columns,
    sortKey,
    sortDirection,
    handleSortChange,
    dialogOpen,
    setDialogOpen,
    editingTask,
    handleAddTask,
    deleteDialogOpen,
    setDeleteDialogOpen,
    deletingTask,
    handleConfirmDelete,
    isDeleting,
  }
}
